/**
 * TrendsSharedAffiliationCard
 *
 * "Shared Patient Provider Affiliation by Service Line" for the Trends tab —
 * affiliation OVER TIME: one stacked 100% bar per quarter (affiliated vs
 * non-affiliated share of shared patients), built entirely from the
 * historical data tree (shared-after / shared-before files on
 * data.doctivityhealth.com) across the configured quarter range. "All"
 * aggregates every serviceline; picking one aggregates just that serviceline
 * (its rollup rows plus its sub-serviceline rows — the duplication cancels
 * out of the percentage since both levels land in the same buckets).
 *
 * Affiliation is resolved client-side: the client's affiliated provider NPIs
 * are fetched ONCE (loadAffiliatedNpis, cached per client in the trending
 * store) and each edge row is a Set lookup — no per-row API calls.
 *
 * Providers only: network edges are provider-to-provider, so the org trees
 * carry no shared-* files and this card renders nothing for organizations.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import Chart from 'react-apexcharts';
import { PropTypes } from 'prop-types';
import { withStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { StoreUtil } from 'doctivity-shared/utils';
import { ColorUtil } from 'utils';
import { LoadingView } from './LoadingView';
import { ServiceLineSelect } from './ServiceLineSelect';
import { TitleTooltip } from './TitleTooltip';
import {
  loadTrendingData,
  loadAffiliatedNpis,
  trendingKey,
  TrendingDataType,
  TrendingEntityType,
} from 'store/actions/trendingActions';
import { listClaimsServicelines } from 'store/actions/claimsActions';
import CLAIMS_CFG from '../../config/claims-date.js';

const styles = (theme) => ({
  filterDropDown: {
    width: 320,
    marginRight: theme.spacing(),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  dropDown: {
    marginBottom: theme.spacing(2),
  },
  dropDownContainer: {
    display: 'flex',
    flexDirection: 'row',
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  filterLabel: {
    backgroundColor: 'white',
    paddingRight: 4,
  },
  noData: {
    textAlign: 'center',
    paddingTop: 50,
    minHeight: 200,
  },
  note: {
    fontSize: 12,
    color: theme.palette.primary.main,
    textAlign: 'right',
  },
});

// Every quarter from trendingStartQuarter through trendingEndQuarter
function trendingQuarters() {
  const { trendingStartQuarter: start, trendingEndQuarter: end } = CLAIMS_CFG;
  const quarters = [];
  let { year, quarter } = start;
  while (year < end.year || (year === end.year && quarter <= end.quarter)) {
    quarters.push({ year, quarter });
    quarter++;
    if (quarter > 4) {
      quarter = 1;
      year++;
    }
  }
  return quarters;
}

function TrendsSharedAffiliationCard({ classes, entityType, entity }) {
  const dispatch = useDispatch();
  const [before, setBefore] = useState(false);
  const [serviceline, setServiceline] = useState('all');
  const quarters = useMemo(() => trendingQuarters(), []);

  const clientId = useSelector((state) => state.app.selectedClient);
  const servicelines = useSelector(
    (state) => state.claims && state.claims.servicelines_grouped,
    shallowEqual,
  );
  const servicelinesList = useSelector(
    (state) => StoreUtil.get(state.claims, 'servicelines_list'),
    shallowEqual,
  );
  const affiliated = useSelector(
    (state) => state.trending.affiliatedByClient[clientId],
    shallowEqual,
  );

  const entityKey =
    entity?.npi || entity?.claims_hms_piid || null;
  const dataType = before
    ? TrendingDataType.SHARED_BEFORE
    : TrendingDataType.SHARED_AFTER;
  const keys = useMemo(
    () =>
      quarters.map((q) =>
        trendingKey(
          TrendingEntityType.PROVIDER,
          entityKey,
          q.year,
          q.quarter,
          dataType,
        ),
      ),
    [entityKey, quarters, dataType],
  );
  const entries = useSelector(
    (state) => keys.map((k) => state.trending.entries[k]),
    shallowEqual,
  );

  useEffect(() => {
    if (!servicelines && StoreUtil.needsLoadNoCache(servicelinesList)) {
      dispatch(listClaimsServicelines());
    }
  }, [dispatch, servicelines, servicelinesList]);

  useEffect(() => {
    if (clientId) dispatch(loadAffiliatedNpis(clientId));
  }, [dispatch, clientId]);

  useEffect(() => {
    if (!entityKey || entityType !== 'providers') return;
    quarters.forEach((q) => {
      dispatch(
        loadTrendingData(
          TrendingEntityType.PROVIDER,
          entityKey,
          q.year,
          q.quarter,
          dataType,
        ),
      );
    });
  }, [dispatch, entityType, entityKey, quarters, dataType]);

  // One aggregated bar per quarter: affiliated vs non-affiliated share of the
  // quarter's shared patients, over all servicelines or one selected one.
  const { categories, series, hasRows } = useMemo(() => {
    const categories = quarters.map((q) => `${q.year} Q${q.quarter}`);
    const perQuarter = [];
    const npiSet = affiliated?.npiSet || new Set();

    // slug filter for the selected serviceline: the parent slug plus its
    // children's. Including both levels does not skew the PERCENTAGE — a
    // relationship counted at parent and child level lands in the same
    // affiliated/non bucket both times, so the ratio is unchanged.
    let acceptSlugs = null; // null = all servicelines
    if (serviceline !== 'all' && servicelines) {
      const parent = servicelines.find((p) => p.id === serviceline);
      if (parent) {
        acceptSlugs = new Set(
          [parent.slug, ...(parent.children || []).map((c) => c.slug)].filter(
            Boolean,
          ),
        );
      }
    }

    let hasRows = false;
    entries.forEach((entry) => {
      if (!entry || !entry.loaded || !entry.data || entry.data.length === 0) {
        perQuarter.push(null); // quarter not uploaded — no bar
        return;
      }
      let affiliatedSum = 0;
      let nonAffiliatedSum = 0;
      entry.data.forEach((row) => {
        if (acceptSlugs && !acceptSlugs.has(row.serviceline)) return;
        // suppressed shared counts (null ≈ <11) count as 5, mirroring the DB
        // analytic's COALESCE so this card and Shared Patients roughly agree
        const patients = row.shared_patients == null ? 5 : row.shared_patients;
        if (npiSet.has(row.provider_npi)) {
          affiliatedSum += patients;
        } else {
          nonAffiliatedSum += patients;
        }
      });
      const total = affiliatedSum + nonAffiliatedSum;
      if (total > 0) hasRows = true;
      perQuarter.push(
        total > 0
          ? {
              total,
              affiliated: affiliatedSum,
              nonAffiliated: nonAffiliatedSum,
              pct: Math.round((affiliatedSum / total) * 100),
            }
          : null,
      );
    });

    // raw aggregated volumes: the BAR HEIGHT tracks total patients that
    // quarter, while labels/tooltips speak only in percentages — the counts
    // aren't unique patients across servicelines, so numbers stay hidden
    const series = [
      {
        name: 'Affiliated',
        data: perQuarter.map((q) => (q ? q.affiliated : null)),
      },
      {
        name: 'Non-Affiliated',
        data: perQuarter.map((q) => (q ? q.nonAffiliated : null)),
      },
    ];
    series.meta = perQuarter;

    return { categories, series, hasRows };
  }, [entries, servicelines, affiliated, serviceline, quarters]);

  // network data is provider-to-provider — nothing to show on org pages
  if (entityType !== 'providers') return null;
  if (!entityKey) return null;

  const loadedCount = entries.filter((e) => e && (e.loaded || e.error)).length;
  const isLoaded =
    loadedCount > 0 && servicelines &&
    affiliated && (affiliated.loaded || affiliated.error);
  const entityName = entity.last_name
    ? `${entity.last_name}, ${entity.first_name}`
    : entity.name;

  return (
    <Card>
      <CardContent>
        <TitleTooltip
          title='Shared Patient Provider Affiliation by Service Line'
          tooltip='Affiliated share of shared patients per quarter, from the historical data. Each quarter reflects the trailing 12 months.'
        />
        {loadedCount < entries.length && (
          <Typography className={classes.note}>
            {`Loading ${loadedCount}/${entries.length} quarters…`}
          </Typography>
        )}
        <div className={classes.dropDownContainer}>
          <FormControl
            variant='outlined'
            className={classes.filterDropDown}
            key='direction'
          >
            <InputLabel className={classes.filterLabel}>Claims Date</InputLabel>
            <Select
              key='select'
              value={before}
              onChange={(e) => setBefore(e.target.value)}
              label='Claims Date'
            >
              <MenuItem value={false} key='after'>
                {`After ${entityName}`}
              </MenuItem>
              <MenuItem value key='before'>
                {`Before ${entityName}`}
              </MenuItem>
            </Select>
          </FormControl>
          <div className={classes.dropDown}>
            <ServiceLineSelect
              value={serviceline}
              onChange={(id) => setServiceline(id)}
              showAll
            />
          </div>
        </div>
        {!isLoaded && <LoadingView />}
        {isLoaded && !hasRows && (
          <Typography className={classes.noData}>No shared patients.</Typography>
        )}
        {isLoaded && hasRows && (
          <Chart
            type='bar'
            height={380}
            options={{
              chart: {
                id: 'TrendsSharedAffiliation',
                toolbar: { show: false },
                animations: { enabled: false },
                stacked: true,
              },
              xaxis: {
                categories,
                tickPlacement: 'on',
              },
              yaxis: {
                // bar height carries relative volume; numbers are hidden
                // because shared patients are not unique across servicelines
                labels: { show: false },
                title: { text: 'Patients' },
              },
              tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                x: {
                  formatter: (x, opt) => categories[opt.dataPointIndex],
                },
                y: {
                  // percentages only — no counts (duplicate patients)
                  formatter: (y, opt) => {
                    const q = series.meta?.[opt.dataPointIndex];
                    if (!q) return null;
                    return opt.seriesIndex === 0
                      ? `${q.pct}%`
                      : `${100 - q.pct}%`;
                  },
                },
              },
              colors: [ColorUtil.AffiliatedColor, ColorUtil.NonAffiliatedColor],
              dataLabels: {
                enabled: true,
                enabledOnSeries: [0], // label the affiliated segment only
                hideOverflowingLabels: true,
                dropShadow: { enabled: true, left: 0, top: 0, opacity: 0.4 },
                formatter: (val, opt) => {
                  const q = series.meta?.[opt.dataPointIndex];
                  return q ? `${q.pct}%` : '';
                },
                offsetY: 0,
              },
              plotOptions: {
                bar: { horizontal: false, columnWidth: '60%' },
              },
              legend: { position: 'bottom' },
            }}
            series={[
              { name: series[0].name, data: series[0].data },
              { name: series[1].name, data: series[1].data },
            ]}
          />
        )}
      </CardContent>
    </Card>
  );
}

TrendsSharedAffiliationCard.propTypes = {
  classes: PropTypes.object,
  entityType: PropTypes.oneOf(['providers', 'organizations']).isRequired,
  entity: PropTypes.object,
};

const Styled = withStyles(styles)(TrendsSharedAffiliationCard);
export { Styled as TrendsSharedAffiliationCard };
